import Head from "next/head";
import Pricing from "components/sections/Pricing";
import Table from "components/sections/Table";
import Plan from "components/sections/Plan";
import Accordion from "components/sections/Accordion";
import GetStarted from "components/sections/GetStarted";
import Tooltip from "components/UI/Tooltip";
import useScrollTop from "../../hooks/useScrollTop";
import Link from "next/link";
import { track } from "../../utils/amplitude";
import { useState } from "react";

const mockItems = [
  {
    id: "1",
    description: (
      <>
        <span>Monthly tracked revenue included</span>
        <Tooltip
          title={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 0C4.48578 0 0 4.48578 0 10C0 15.5142 4.48578 20 10 20C15.5142 20 20 15.5142 20 10C20 4.48578 15.5142 0 10 0ZM10 15.8333C9.53995 15.8333 9.16672 15.4601 9.16672 15C9.16672 14.5399 9.53995 14.1667 10 14.1667C10.4601 14.1667 10.8333 14.5399 10.8333 15C10.8333 15.4601 10.4601 15.8333 10 15.8333ZM11.3191 10.535C11.0242 10.6708 10.8333 10.9683 10.8333 11.2926V11.6667C10.8333 12.1266 10.4608 12.5 10 12.5C9.53918 12.5 9.16672 12.1266 9.16672 11.6667V11.2926C9.16672 10.32 9.73831 9.42841 10.6209 9.02084C11.47 8.63007 12.0833 7.59247 12.0833 7.08328C12.0833 5.93506 11.1491 5 10 5C8.85086 5 7.91672 5.93506 7.91672 7.08328C7.91672 7.54333 7.5441 7.91672 7.08328 7.91672C6.62247 7.91672 6.25 7.54333 6.25 7.08328C6.25 5.01587 7.93243 3.33328 10 3.33328C12.0676 3.33328 13.75 5.01587 13.75 7.08328C13.75 8.20923 12.7733 9.8642 11.3191 10.535Z"
                fill="currentColor"
              />
            </svg>
          }
        >
          <div>
            Monthly Tracked Revenue is calculated across all apps. For each app,
            MTR is the revenue in USD reported to Apphud during one billing
            period before Apple cut. We count only real transactions, sandbox
            purchases are not counted.
          </div>
        </Tooltip>
      </>
    ),
    col1Text: "$10,000",
    col2Text: "$5,000",
    col3Text: "$5,000",
    col4Text: "$100,000",
  },

  {
    id: "2",
    description: "Integrate and handle cross-platform in-app subscriptions",
    col1Check: "check",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "3",
    description: "Validate App Store and Google Play receipts",
    col1Check: "check",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "4",
    description: "Implement subscription offers with no server code",
    col1Check: "check",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "4_1",
    description: "StoreKit 2",
    col1Check: "check",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "4_2",
    description: "Google Billing V5",
    col1Check: "check",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "5",
    description: "Revenue analytics",
    col1Check: "check",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },
  {
    id: "6",
    description: "Detailed user page with transaction history",
    col1Check: "check",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },
  {
    id: "7",
    description: "Saved charts",
    col1Check: "close",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },
  {
    id: "8",
    description: "ARPU/ARPPU/ARPAS, churn analysis, conversion funnels",
    col1Check: "close",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },
  {
    id: "7",
    description: "Cohort analysis",
    col1Check: "close",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },
  {
    id: "7",
    description: "Integrations with 20+ external tools",
    col1Check: "close",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "9_1",
    description: "Manual and automated push notifications",
    col1Check: "close",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },
  {
    id: "9_1",
    description: "Visual screen builder",
    col1Check: "close",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },
  {
    id: "9_1",
    description: "Web-to-App solution",
    col1Check: "close",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },
  {
    id: "9_3",
    description: "Pricing and paywall experiments",
    col1Check: "close",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "9_4",
    description: "Placements in experiments",
    col1Check: "close",
    col2Check: "check",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "10_0",
    description: "LTV Predictions",
    col1Check: "close",
    col2Check: "close",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "10",
    description: "Server-to-server webhooks",
    col1Close: "close",
    col2Close: "close",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "11",
    description: "Customers API",
    col1Close: "close",
    col2Close: "close",
    col3Check: "check",
    col4Check: "check",
  },

  {
    id: "12",
    description: "On-demand raw data exports",
    col1Close: "close",
    col2Close: "close",
    col3Close: "check",
    col4Check: "check",
  },

  {
    id: "13_0",
    description: "Daily Data Exports",
    col1Close: "close",
    col2Close: "close",
    col3Close: "check",
    col4Check: "check",
  },

  {
    id: "13",
    description: "Seats",
    col1Text: "1 seat",
    col2Text: "5 seats",
    col3Text: "10 seats",
    col4Text: "Unlimited",
  },

  {
    id: "13_1",
    description: "Support",
    col1Text: "Email and community",
    col2Text: "Online chat",
    col3Text: "Higher priority chat",
    col4Text: "Premium support",
  },
];

const mockTitlesMonthly = [
  {
    id: "0",
    description: "",
  },

  {
    id: "1",
    description: "Free",
    subescription: "$0 / month",
  },
  {
    id: "3",
    description: "Pro",
    subescription: "$49 / month",
  },
  {
    id: "4",
    description: "Expert",
    subescription: "$59 / month",
  },
  {
    id: "5",
    description: "Enterprise",
    subescription: "Contact us",
  },
];

const mockTitlesAnnually = [
  {
    id: "0",
    description: "",
  },

  {
    id: "1",
    description: "Free",
    subescription: "$0 / month",
  },
  {
    id: "3",
    description: "Pro",
    subescription: "$49 / month",
  },
  {
    id: "4",
    description: "Expert",
    subescription: "$59 / month",
  },
  {
    id: "5",
    description: "Enterprise",
    subescription: "Contact us",
  },
];

export default function pricing() {
  useScrollTop();
  const [planType, setPlanType] = useState(0);
  const period = planType ? "annually" : "monthly";
  return (
    <div className="wrapper">
      <Head>
        <title>Price - Apphud</title>
        <link rel="canonical" href="https://apphud.com/pricing" />
        <meta property="og:title" content="Price - Apphud" />
        <meta
          property="og:description"
          content="Good price: pay as you grow. Use Apphud for free if your revenue is less than $10,000 per month."
        />
        <meta property="og:url" content="https://apphud.com/pricing" />
        <meta
          property="og:image"
          content="/images/og/im_8.webp"
          key="og:image"
        />
        <meta
          property="twitter:image"
          content="/images/og/im_8.webp"
          key="twitter:image"
        />
        <meta property="twitter:title" content="Price - Apphud" />
        <meta
          property="twitter:description"
          content="Good price: pay as you grow. Use Apphud for free if your revenue is less than $10,000 per month."
        />
        <meta property="twitter:url" content="https://apphud.com/pricing" />
        <meta
          name="description"
          content="Good price: pay as you grow. Use Apphud for free if your revenue is less than $10,000 per month."
        />
      </Head>

      <div className="title-block">
        <div className="container">
          <h1>Simple affordable pricing</h1>
          <p className="text_xl">
            Choose a plan that fits your budget and goals. Start for free with
            no credit card required.
          </p>
        </div>
      </div>

      <Pricing planType={planType} setPlanType={setPlanType} />

      <Table
        titles={planType === 1 ? mockTitlesAnnually : mockTitlesMonthly}
        items={mockItems}
        fixedHeader
        col3
      />

      <Plan />
      <div className="accordion">
        <div className="container">
          <h2>Got a question?</h2>
          <div className="accordion-items">
            <Accordion title="What is MTR?">
              Monthly Tracked Revenue is calculated across all apps. For each
              app, MTR is the revenue in USD reported to Apphud during one
              billing period before Apple cut. We count only real transactions,
              sandbox purchases are not counted.
            </Accordion>
            <Accordion title="What if I go over MTR included to my plan?">
              Once you’ve run out of MTR included in your plan you will be
              charged additional overage rate:
              <ul>
                <li>$9.99 per additional $1,000 MTR on Pro plan</li>
                <li>$11.99 per additional $1,000 MTR on Expert plan</li>
                <li>Custom rate on Enterprise plan</li>
              </ul>
            </Accordion>
            <Accordion title="I’m on a Free plan. What if my MTR goes over $10,000?">
              We don't stop collecting data for the weekly grace period – for
              the next 7 days Apphud SDK will continue working as normal.
              <br />
              <br /> After grace period is over we will still handle IAP
              purchases, but won't track subscription renewals, and you won’t
              have access to the dashboard.
            </Accordion>
            <Accordion title="What’s the difference between paid plans?">
              Enterprise plan will be cheaper if your MTR is high. There are
              several premium features available on paid plans only. View the
              list of them above.
            </Accordion>
            <Accordion title="Are all features included to all plans?">
              No. View the list of features available on paid plans only above.
            </Accordion>
            <Accordion title="Read the documentation or contact us.">
              Explore the{" "}
              <a
                href="https://docs.apphud.com/"
                onClick={() =>
                  track("page_external_link_clicked", {
                    target_url: "https://docs.apphud.com/",
                  })
                }
              >
                docs
              </a>{" "}
              and feel free to{" "}
              <Link href="/contact">
                <a
                  onClick={() =>
                    track("page_external_link_clicked", {
                      target_url: "https://apphud.com/contact",
                    })
                  }
                >
                  reach out
                </a>
              </Link>
              .
            </Accordion>
          </div>
        </div>
      </div>
      <GetStarted />
    </div>
  );
}
